import React, { CSSProperties, FC, ReactElement, useEffect, useState } from 'react';
import { useLocale } from '../../contexts/localeContext';
import { usePagination } from '../../contexts/table/PaginationContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import { PaginationModel } from '../../models/Pagination';
import ItemSelector from '../item-selector/item-selector';
import Stepper from '../stepper/stepper';

import './paginator.css';

/**
 *
 * The idea is that as you move along the pages, it keeps
 * 2 pages before and 2 pages after the current page in view.
 *
 * Example:
 * [1] 2 3 4 5
 * next
 * 1 [2] 3 4 5
 * next
 * 1 2 [3] 4 5
 * next
 * 2 3 [4] 5 6
 * next
 * 3 4 [5] 6 7
 *
 * @param {*} itemCount
 * @param {*} pageSize
 * @param {*} pageNumber
 * @param {*} totalPages
 */
function getPagesForItemsWithSize(itemCount, pageSize, pageNumber, totalPages) {
    const selectionSize = totalPages < 5 ? totalPages : 5;

    if (totalPages <= 1) {
        return [0]; // Only have the 1 page.
    }

    // totalPages <= 5, just show all for selection
    // Or, if current page is either 1, 2 or 3, then just show the 1st 5 pages.
    if (totalPages <= 5 || pageNumber <= 3) {
        const pages = new Array(0);
        for (let i = 0; i < selectionSize; i++) {
            pages.push(i);
        }
        return pages;
    }

    // Last few pages. I.e. not enough to show 2 pages after selected page.
    // Determine how many pages after selected are available and therefore how many pages
    // before to show. E.g. if page 7 of 7 is selected, then show pages 3, 4, 5, 6, 7
    else if (pageNumber >= (totalPages - 1)) {
        const pages = new Array(0);
        let numPagesBackwards = (totalPages - pageNumber);
        let index = ((totalPages - 3) - (numPagesBackwards === 0 ? 1 : numPagesBackwards)) - 1; // -1 is for zero-based index.
        for (let i = index; i < selectionSize + index; i++) {
            pages.push(i);
        }
        return pages;
    }

    // > 10 pages for selection. Show only 10, around the selected page number.
    else {
        const pages = new Array(0);
        let index = (pageNumber - 2) - 1; // -1 is for zero-based index.
        for (let i = index; i < selectionSize + index; i++) {
            pages.push(i);
        }
        return pages;
    }
}

function getStart(page, size) {
    return (page * size) - (size)
}
function getEnd(page, size, totalCount) {
    let ans = (page * size);
    if (ans > totalCount) return totalCount - 1;
    return ans - 1;
}

interface PaginatorProps {
    compact?: boolean;
    itemCount: number;
    children({ start, end }): ReactElement;
    sizes?: number[];
    paginationObj: PaginationModel;
    onPageNumberUpdated: (page: number) => void;
    onPageSizeUpdated?: (size: number) => void;
    isTable?: boolean;
    height?: string;
    style?: CSSProperties;
}

const Paginator: FC<PaginatorProps> = ({
    compact = false,
    itemCount,
    children,
    sizes = [10, 50, 100],
    paginationObj,
    onPageNumberUpdated,
    onPageSizeUpdated,
    isTable,
    style ={}
}) => {
    const { translate } = useLocale()
    const paginationContext = usePagination();

    const createState = (newPagination?) => {
        const totalRecords = (!!paginationContext.pagination.totalCount ? paginationContext.pagination.totalCount : itemCount);
        const totalPages = (!!paginationContext.pagination.totalPages ? paginationContext.pagination.totalPages : 0);
        const pageNumber = (!!paginationContext.pagination.pageNumber ? paginationContext.pagination.pageNumber : 0);
        const size = (!!paginationContext.pagination.maxPageSize ? paginationContext.pagination.maxPageSize : sizes[0]);
        const items = getPagesForItemsWithSize(totalRecords, size, pageNumber, totalPages).map(x => x + 1);
        return {
            maxPageSize: size,
            totalCount: totalRecords,
            totalPages: totalPages,
            pageNumber: pageNumber,
            pages: items,
            sizes: paginationContext.pagination.sizes
        }
    }

    const handlePageNumberUpdate = (pageNumber: number) => {
        if (paginationContext?.isInUse) {
            paginationContext.updatePageNumber(pageNumber);
        }
        else {
            setState({
                ...state,
                pageNumber
            });

            if (!!onPageNumberUpdated) {
                onPageNumberUpdated(pageNumber);
            }
        }
    };

    const handlePageSizeUpdate = (pageSize: number) => {
        if (paginationContext?.isInUse) {
            paginationContext.updatePageSize(pageSize);
        }
        else {
            setState({
                ...state,
                maxPageSize: pageSize
            });

            if (!!onPageSizeUpdated) {
                onPageSizeUpdated(pageSize);
            }
        }
    };

    const handlePrevious = () => {
        const previousPage = state.pageNumber - 1;
        handlePageNumberUpdate(previousPage)
    };

    const handleNext = () => {
        const nextPage = state.pageNumber + 1;
        handlePageNumberUpdate(nextPage);
    };

    useEffect(() => {
        setState(createState(paginationContext.pagination));
    }, [paginationContext.pagination])

    const [state, setState] = useState<PaginationModel>(createState());

    // Force a refresh if the pagination details have changed
    useEffect(() => {
        if (paginationObj !== undefined && !paginationContext?.isInUse) {
            setState(createState(paginationObj));
        }
    }, [paginationObj])

    return <div id={isTable ? 'tablePaginationId' : 'paginationId'} style={style} className={isTable ? 'flex flex-col flex-1 min-h-0' : ''}>
        <div className={isTable ? 'flex-1 min-h-0 overflow-y-auto' : ''}>
            {children({
                start: getStart(state.pageNumber, state.maxPageSize),
                end: getEnd(state.pageNumber, state.maxPageSize, paginationObj ? paginationObj.totalCount : state.totalCount)
            })}
        </div>
        {/* No use in showing pagination if there are no records */}
        {paginationContext.pagination.totalCount !== 0 && <div className="flex justify-between text-xs flex-shrink-0" >
            {!onPageNumberUpdated
                ? null
                : <div className="flex items-center" id="pagination">
                    <Stepper
                        items={state.pages}
                        selected={state.pageNumber}
                        onPrevious={handlePrevious}
                        onNext={handleNext}>
                        <ItemSelector
                            items={state.pages}
                            onSelect={handlePageNumberUpdate}
                            selected={state.pageNumber}
                            selectedClassName='border border-solid border-portal-blue-primary rounded-sm font-flatter px-1'>
                            <span className="px-1"></span>
                        </ItemSelector>
                    </Stepper>
                </div>
            }
            <div className="flex flex-row">
                {!onPageSizeUpdated || sizes.length === 1
                    ? null
                    : <div className="flex flex-row">
                        {compact
                            ? null
                            : <>
                                <span className="mr-1 select-none">{translate.byKey(TranslationKeys.showing)}:</span>
                                <ItemSelector
                                    items={state.sizes}
                                    onSelect={handlePageSizeUpdate}
                                    selected={state.maxPageSize}>
                                    <span className="px-1">/</span>
                                </ItemSelector>
                            </>
                        }
                    </div>}

                <span className="pl-4 select-none" id="totalRecords">{`${getStart(state.pageNumber, state.maxPageSize) + 1}-`
                    + `${getEnd(state.pageNumber, state.maxPageSize, state.totalCount) + 1}`
                    + ` ${translate.byKey(TranslationKeys.of)} `
                    + `${(state.totalCount)}`}
                </span>
            </div>
        </div>}
    </div>
};

export default Paginator;
